import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  VStack,
  Heading,
  Text,
  Box,
  SimpleGrid,
  LinkBox,
  LinkOverlay,
  useColorMode,
  Flex,
  Input,
  IconButton,
  Spinner,
  HStack,
  Avatar,
  useToast,
  Badge,
} from "@chakra-ui/react";
import { useMutation, useQuery, useSubscription } from "@apollo/client";
import { brandConfig, getColor } from "../../brandConfig";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import {
  CHAT_ROOM_BY_SLUG,
  ENSURE_CHAT_ROOM,
  JOIN_CHAT_ROOM,
  CHAT_MESSAGES,
  SEND_CHAT_MESSAGE,
  CHAT_MESSAGE_ADDED_SUBSCRIPTION,
  CHAT_TYPING_SUBSCRIPTION,
  UPDATE_TYPING_STATUS,
} from "../seamless-chat/queries";

interface CheatsheetCard {
  title: string;
  description: string;
  path: string;
}

const cheatsheets: CheatsheetCard[] = [
  {
    title: "Foundation Package Overview",
    description: "What you get with the Foundation subscription ($97/mo) -- Auth, Profile, CRM, Pipeline, and Website Hosting.",
    path: "/business-builder/cheatsheets/foundation-overview",
  },
  {
    title: "Subscription + LM Course Pattern",
    description: "How to sell software as a subscription with auto-enrollment into an LM course that onboards, teaches, and upsells.",
    path: "/business-builder/cheatsheets/subscription-lm-pattern",
  },
  {
    title: "Subscriptions + LM + Rewards Pattern",
    description: "The complete platform pattern: sell software modules via subscriptions, LM course onboarding, auto-granted permissions, and reward token retention.",
    path: "/business-builder/cheatsheets/subscription-rewards-pattern",
  },
  {
    title: "Seamless Chat Module",
    description: "Zero-friction chat: SMS link → anonymous identity → instant messaging → progressive auth to phone login. Architecture & flow.",
    path: "/business-builder/cheatsheets/seamless-chat-module",
  },
  {
    title: "AI Assistant — Quick Reference",
    description: "Your AI-powered business automation assistant. Quick commands, common workflows, safety guardrails, and pro tips for managing your pipeline with natural language.",
    path: "/business-builder/cheatsheets/ai-assistant",
  },
];


// localStorage keys for anonymous chat identity
const ANON_TOKEN_KEY = "chat_anon_token";
const ANON_NAME_KEY = "chat_display_name";
const getAnonToken = () => localStorage.getItem(ANON_TOKEN_KEY);
const setAnonToken = (token: string) => localStorage.setItem(ANON_TOKEN_KEY, token);
const getDisplayName = () => localStorage.getItem(ANON_NAME_KEY);
const setDisplayNameStorage = (name: string) => localStorage.setItem(ANON_NAME_KEY, name);

import { ChatMessage as ChatMsg, ChatRoom as ChatRoomType } from "../../generated/graphql";

const CHAT_SLUG = "cheatsheets-chat"; // default room slug for this page

/** Inline chat widget that embeds directly on the page */
const EmbeddedChat: React.FC = () => {
  const toast = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const [joined, setJoined] = useState(false);
  const [participantId, setParticipantId] = useState("");
  const [displayName, setDisplayName] = useState(getDisplayName() || "");
  const [nameInput, setNameInput] = useState("");
  const [messageInput, setMessageInput] = useState("");
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [typingUsers, setTypingUsers] = useState<Map<string, string>>(new Map());

  const [room, setRoom] = useState<ChatRoomType | null>(null);
  const [roomLoading, setRoomLoading] = useState(true);
  const [ensureRoom] = useMutation(ENSURE_CHAT_ROOM);

  // Auto-create room on mount
  useEffect(() => {
    ensureRoom({
      variables: {
        slug: CHAT_SLUG,
        title: "Cheatsheets Chat",
        description: "Chat about Business Builder patterns and cheatsheets",
      },
    })
      .then(({ data }) => {
        setRoom(data?.ensureChatRoom);
        setRoomLoading(false);
      })
      .catch(() => setRoomLoading(false));
  }, [ensureRoom]);

  const { data: messagesData, loading: messagesLoading } = useQuery(CHAT_MESSAGES, {
    variables: { roomId: room?.id, limit: 50 },
    skip: !room?.id || !joined,
    fetchPolicy: "network-only",
  });

  const [joinRoom] = useMutation(JOIN_CHAT_ROOM);
  const [sendMessage] = useMutation(SEND_CHAT_MESSAGE);
  const [updateTyping] = useMutation(UPDATE_TYPING_STATUS);

  useSubscription(CHAT_MESSAGE_ADDED_SUBSCRIPTION, {
    variables: { roomId: room?.id },
    skip: !room?.id || !joined,
    onData: ({ data }) => {
      const newMsg = data?.data?.chatMessageAdded;
      if (newMsg) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });
      }
    },
  });

  useSubscription(CHAT_TYPING_SUBSCRIPTION, {
    variables: { roomId: room?.id },
    skip: !room?.id || !joined,
    onData: ({ data }) => {
      const update = data?.data?.chatTypingUpdate;
      if (update && update.participantId !== participantId) {
        setTypingUsers((prev) => {
          const next = new Map(prev);
          if (update.isTyping) next.set(update.participantId, update.displayName);
          else next.delete(update.participantId);
          return next;
        });
      }
    },
  });

  useEffect(() => {
    if (messagesData?.chatMessages) {
      setMessages([...messagesData.chatMessages].reverse());
    }
  }, [messagesData]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Auto-join: if logged in (auth_token) or returning anonymous (anon token)
  useEffect(() => {
    if (!room?.id || joined) return;
    const hasAuth = !!localStorage.getItem("auth_token");
    const hasAnon = !!getAnonToken();
    if (hasAuth || hasAnon) {
      handleJoin(getDisplayName() || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.id]);

  const handleJoin = async (name: string) => {
    if (!room?.id) return;
    try {
      const { data } = await joinRoom({
        variables: {
          roomId: room.id,
          displayName: name || undefined,
          anonToken: getAnonToken() || undefined,
        },
      });
      const result = data?.joinChatRoom;
      if (result) {
        if (result.anonToken) setAnonToken(result.anonToken);
        setParticipantId(result.participantId);
        setDisplayName(result.displayName);
        setDisplayNameStorage(result.displayName);
        setJoined(true);
      }
    } catch (err: any) {
      toast({ title: "Failed to join", description: err.message, status: "error", duration: 3000 });
    }
  };

  const handleSend = useCallback(async () => {
    if (!messageInput.trim() || !room?.id) return;
    const text = messageInput.trim();
    setMessageInput("");
    try {
      await sendMessage({
        variables: { input: { roomId: room.id, content: text }, anonToken: getAnonToken() },
      });
      updateTyping({ variables: { roomId: room.id, isTyping: false, anonToken: getAnonToken() } }).catch(() => {});
    } catch (err: any) {
      toast({ title: "Failed to send", description: err.message, status: "error", duration: 2000 });
      setMessageInput(text);
    }
  }, [messageInput, room?.id, sendMessage, updateTyping, toast]);

  const handleTyping = () => {
    if (!room?.id) return;
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    updateTyping({ variables: { roomId: room.id, isTyping: true, anonToken: getAnonToken() } }).catch(() => {});
    typingTimeoutRef.current = setTimeout(() => {
      updateTyping({ variables: { roomId: room.id, isTyping: false, anonToken: getAnonToken() } }).catch(() => {});
    }, 3000);
  };

  // Room failed to create/load
  if (!roomLoading && !room) {
    return (
      <Box
        p={6}
        borderRadius="lg"
        bg="rgba(20,20,20,0.6)"
        border="1px solid rgba(255,255,255,0.1)"
        textAlign="center"
      >
        <Text color="rgba(255,255,255,0.5)" fontSize="sm">
          Chat unavailable. Check that the backend is running.
        </Text>
      </Box>
    );
  }

  if (roomLoading) {
    return (
      <Flex justify="center" py={6}>
        <Spinner size="md" color={brandConfig.colors.primary} />
      </Flex>
    );
  }

  // Join prompt
  if (!joined && room) {
    return (
      <Box
        p={6}
        borderRadius="lg"
        bg="rgba(20,20,20,0.8)"
        border="1px solid rgba(255,255,255,0.1)"
      >
        <VStack spacing={4} align="stretch">
          <Heading size="sm" color="white">
            {room.title}
          </Heading>
          {room.description && (
            <Text fontSize="sm" color="rgba(255,255,255,0.5)">{room.description}</Text>
          )}
          <HStack>
            <Input
              placeholder="Your name (optional)"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleJoin(nameInput || "Anonymous"); }}
              bg="rgba(30,30,35,0.8)"
              border="1px solid rgba(255,255,255,0.15)"
              color="white"
              _placeholder={{ color: "rgba(255,255,255,0.4)" }}
              _focus={{ borderColor: brandConfig.colors.primary }}
              size="md"
            />
            <IconButton
              aria-label="Join"
              onClick={() => handleJoin(nameInput || "Anonymous")}
              bg={brandConfig.colors.primary}
              color="white"
              _hover={{ bg: brandConfig.colors.primaryHover }}
              icon={<Text fontSize="sm">Join</Text>}
            />
          </HStack>
          <Text fontSize="xs" color="rgba(255,255,255,0.3)">
            No account needed. Pick a name and start chatting.
          </Text>
        </VStack>
      </Box>
    );
  }

  // Live chat
  if (!room) return null;
  return (
    <Box
      borderRadius="lg"
      bg="rgba(20,20,20,0.8)"
      border="1px solid rgba(255,255,255,0.1)"
      overflow="hidden"
    >
      {/* Header */}
      <Flex
        px={4} py={3}
        bg="rgba(15,15,15,0.95)"
        borderBottom="1px solid rgba(255,255,255,0.08)"
        align="center"
        justify="space-between"
      >
        <HStack spacing={2}>
          <Text fontWeight="bold" color="white" fontSize="sm">{room.title}</Text>
          <Badge colorScheme="green" fontSize="2xs">LIVE</Badge>
        </HStack>
        <HStack spacing={2}>
          <Avatar size="xs" name={displayName} bg={brandConfig.colors.primary} color="white" />
          <Text fontSize="xs" color="rgba(255,255,255,0.6)">{displayName}</Text>
        </HStack>
      </Flex>

      {/* Messages */}
      <Box h="350px" overflowY="auto" px={4} py={2}>
        {messagesLoading && (
          <Flex justify="center" py={4}><Spinner size="sm" color={brandConfig.colors.primary} /></Flex>
        )}
        {messages.length === 0 && !messagesLoading && (
          <Flex justify="center" py={8}>
            <Text fontSize="sm" color="rgba(255,255,255,0.3)">No messages yet. Say hello!</Text>
          </Flex>
        )}
        {messages.map((msg) => {
          const isOwn = msg.senderId === participantId;
          if (msg.senderType === "SYSTEM") {
            return (
              <Flex key={msg.id} justify="center" my={2}>
                <Text fontSize="xs" color="rgba(255,255,255,0.4)" fontStyle="italic">{msg.content}</Text>
              </Flex>
            );
          }
          return (
            <Flex key={msg.id} justify={isOwn ? "flex-end" : "flex-start"} mb={2}>
              <VStack align={isOwn ? "flex-end" : "flex-start"} spacing={0} maxW="75%">
                {!isOwn && (
                  <Text fontSize="2xs" color="rgba(255,255,255,0.4)" px={2}>{msg.senderName}</Text>
                )}
                <Box
                  px={3} py={1.5}
                  borderRadius="lg"
                  bg={isOwn ? brandConfig.colors.primary : "rgba(40,40,45,0.9)"}
                  color="white"
                  borderBottomRightRadius={isOwn ? "4px" : "lg"}
                  borderBottomLeftRadius={isOwn ? "lg" : "4px"}
                >
                  <Text fontSize="sm" whiteSpace="pre-wrap">{msg.content}</Text>
                </Box>
                <Text fontSize="2xs" color="rgba(255,255,255,0.25)" px={2}>
                  {new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </Text>
              </VStack>
            </Flex>
          );
        })}
        <div ref={messagesEndRef} />
      </Box>

      {/* Typing indicator */}
      {typingUsers.size > 0 && (
        <Box px={4} py={1}>
          <Text fontSize="xs" color="rgba(255,255,255,0.4)" fontStyle="italic">
            {Array.from(typingUsers.values()).join(", ")} typing...
          </Text>
        </Box>
      )}

      {/* Input */}
      <Flex
        px={4} py={3}
        bg="rgba(15,15,15,0.95)"
        borderTop="1px solid rgba(255,255,255,0.08)"
        gap={2}
      >
        <Input
          placeholder="Type a message..."
          value={messageInput}
          onChange={(e) => { setMessageInput(e.target.value); handleTyping(); }}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          bg="rgba(30,30,35,0.8)"
          border="1px solid rgba(255,255,255,0.15)"
          color="white"
          _placeholder={{ color: "rgba(255,255,255,0.4)" }}
          _focus={{ borderColor: brandConfig.colors.primary }}
          size="sm"
          flex={1}
        />
        <IconButton
          aria-label="Send"
          onClick={handleSend}
          isDisabled={!messageInput.trim()}
          bg={brandConfig.colors.primary}
          color="white"
          _hover={{ bg: brandConfig.colors.primaryHover }}
          _disabled={{ opacity: 0.5 }}
          size="sm"
          icon={<Text fontSize="xs">Send</Text>}
        />
      </Flex>
    </Box>
  );
};

const CheatsheetIndex: React.FC = () => {
  const { colorMode } = useColorMode();
  const bgMain = getColor("background.main", colorMode);
  const textPrimary = getColor("text.primary", colorMode);

  return (
    <Box bg={bgMain} minHeight="100vh" display="flex" flexDirection="column">
      <NavbarWithCallToAction />

      <Container maxW="container.lg" py={10} flex="1">
        <VStack spacing={6} align="stretch">
          <Heading size="lg" color={textPrimary}>
            Business Builder Cheatsheets
          </Heading>
          <Text color={brandConfig.colors.text.secondary} fontSize="sm">
            Visual guides for the Business Builder platform
          </Text>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
            {cheatsheets.map((sheet) => (
              <LinkBox
                key={sheet.path}
                p={6}
                bg={brandConfig.colors.background.surface}
                borderRadius="lg"
                border="1px solid"
                borderColor={brandConfig.colors.border.light}
                _hover={{
                  borderColor: brandConfig.colors.primary,
                  shadow: "md",
                  transform: "translateY(-2px)",
                  transition: "all 0.2s",
                }}
                transition="all 0.2s"
              >
                <VStack align="start" spacing={2}>
                  <LinkOverlay as={RouterLink} to={sheet.path}>
                    <Heading size="sm" color={brandConfig.colors.primary}>
                      {sheet.title}
                    </Heading>
                  </LinkOverlay>
                  <Text fontSize="sm" color={brandConfig.colors.text.secondary}>
                    {sheet.description}
                  </Text>
                </VStack>
              </LinkBox>
            ))}
          </SimpleGrid>

          <LinkBox
            mt={10}
            p={6}
            bg={brandConfig.colors.background.surface}
            borderRadius="lg"
            border="1px solid"
            borderColor={brandConfig.colors.border.light}
            _hover={{
              borderColor: "#dc2626",
              shadow: "md",
              transform: "translateY(-2px)",
              transition: "all 0.2s",
            }}
            transition="all 0.2s"
          >
            <VStack align="start" spacing={2}>
              <LinkOverlay as={RouterLink} to="/energy-research">
                <Heading size="sm" color="#dc2626">
                  Energy & Geopolitics Research
                </Heading>
              </LinkOverlay>
              <Text fontSize="sm" color={brandConfig.colors.text.secondary}>
                Australia's energy crisis, Japan's fuel strategy, oil reserves, supply routes, key decision makers, and research checklist -- all in one place.
              </Text>
            </VStack>
          </LinkBox>

          {/* Live Chat Section */}
          <Box mt={10}>
            <Heading size="md" color={textPrimary} mb={2}>
              Live Chat
            </Heading>
            <Text color={brandConfig.colors.text.secondary} fontSize="sm" mb={4}>
              Anonymous real-time chat -- no login required. Try it out below.
            </Text>
            <EmbeddedChat />
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default CheatsheetIndex;
